import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { OptionsHomeTasks } from "../home/components/OptionsHomeTasks";
import { ProjectsHeader } from "./components/projectsPage/ProjectsHeader";
import projectsStyles from './styles/projects.module.scss';
import ProjectsList from "./components/projectsPage/ProjectsList";
import { loadProjects } from "./slices/projectsSlice";
import { IProjectsData } from "@/api/projects/types";
import { useGetAllProjects } from "./hooks/useGetAllProjects";
import { RootState } from "../../app/store";
import { CircularLoader } from "../../components/Layout/CircularLoader";
import { useI18n } from "../i18n/useI18n";
import UIcon from "../../assets/uicons/UIcon";
import Paginator from "../../components/UI/Paginator/Paginator";
import ProjectFormDrawer from "./components/projectsPage/ProjectFormDrawer";

export const Projects = () => {
  const dispatch = useDispatch();
  const { t } = useI18n();
  
  // Estado para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10); // Proyectos por página
  
  const { data, isSuccess, refetch, isLoading, isFetching } = useGetAllProjects(currentPage, limit);
  const { projects } = useSelector((state: RootState) => state.projects);
  
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<IProjectsData | undefined>();

  const initVisibility = {
    recentProject: false,
    otherProject: false,
  };
  const [visibility, setVisibility] = useState(initVisibility);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  const handleOpenDrawer = (project?: IProjectsData) => {
    setSelectedProject(project);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedProject(undefined);
  };

  const setTaksVilivility = (data: string) => {
    switch (data) {
      case "recentProject":
        setVisibility({
          ...visibility,
          recentProject: !visibility.recentProject,
        });
        break;
      case "otherProject":
        setVisibility({ ...visibility, otherProject: !visibility.otherProject });
        break;
      default:
        setVisibility(initVisibility);
    }
  };

  const getInitData = useCallback(async () => {
    if (data && isSuccess) {
      dispatch(loadProjects({
        success: true,
        message: "Proyectos cargados",
        data: data.data
      }));
    }
  }, [data, isSuccess, dispatch]);

  useEffect(() => {
    getInitData();
  }, [getInitData]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll suave hacia arriba cuando cambia de página
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calcular total de páginas desde la respuesta de la API
  const totalPages = data?.totalPages || 1;

  return (
    <>
      <ProjectsHeader onCreateProject={() => handleOpenDrawer()} />
      <ProjectFormDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        projectData={selectedProject}
        refetchProjects={refetch}
      />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px 16px 32px' }}>
        <OptionsHomeTasks
          visibilityTasks={visibility.recentProject}
          setTaksVilivility={setTaksVilivility}
          taksassing={"recentProject"}
          text={t("module_projects.module_recent_list_title")}
        />
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            aria-label="Vista de lista"
            onClick={() => setViewMode('list')}
            style={{
              background: viewMode === 'list' ? 'var(--color-primary-500, #3b82f6)' : 'transparent',
              color: viewMode === 'list' ? '#fff' : 'var(--color-primary-500, #3b82f6)',
              border: 'none',
              borderRadius: 6,
              padding: 6,
              cursor: 'pointer',
              transition: 'background 0.2s',
              outline: viewMode === 'list' ? '2px solid var(--color-primary-500, #3b82f6)' : 'none',
            }}
          >
            <UIcon name="list" size={22} />
          </button>
          <button
            aria-label="Vista de grid"
            onClick={() => setViewMode('grid')}
            style={{
              background: viewMode === 'grid' ? 'var(--color-primary-500, #3b82f6)' : 'transparent',
              color: viewMode === 'grid' ? '#fff' : 'var(--color-primary-500, #3b82f6)',
              border: 'none',
              borderRadius: 6,
              padding: 6,
              cursor: 'pointer',
              transition: 'background 0.2s',
              outline: viewMode === 'grid' ? '2px solid var(--color-primary-500, #3b82f6)' : 'none',
            }}
          >
            <UIcon name="grid" size={22} />
          </button>
        </div>
      </div>
      
      {/* Indicador de carga para cambios de página */}
      {isFetching && !isLoading && (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px', 
          color: 'var(--text-muted, #6b7280)',
          fontSize: '14px'
        }}>
          Cargando proyectos...
        </div>
      )}
      
      {isLoading ? (
        <CircularLoader />
      ) : projects.length !== 0 && isSuccess ? (
        <>
          <div
            className={
              `${viewMode === 'grid'
                ? projectsStyles["body-project-items"] + ' ' + projectsStyles["project-list-grid"]
                : projectsStyles["body-project-items"]} `
            }
            style={viewMode === 'grid' ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 } : {}}
          >
            {projects?.map((project: IProjectsData, key: number) => {
              return !visibility.recentProject ? (
                <ProjectsList project={project} key={key} viewMode={viewMode} onEdit={handleOpenDrawer} />
              ) : null;
            })}
          </div>
          
          {/* Información de paginación */}
          {totalPages > 1 && (
            <div style={{ 
              margin: '16px 0', 
              textAlign: 'center', 
              color: 'var(--text-muted, #6b7280)',
              fontSize: '14px'
            }}>
              Mostrando página {currentPage} de {totalPages} ({data?.total || 0} proyectos total)
            </div>
          )}
          
          <div style={{ margin: '32px 0', display: 'flex', justifyContent: 'center' }}>
            <Paginator
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted, #6b7280)' }}>
          No se encontraron proyectos
        </div>
      )}
    </>
  );
};
